import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';

import { referralTerms } from './referralTerms';

interface ReferralTermsModalProps {
  onClose: () => void;
}

const ReferralTermsModal = ({ onClose }: ReferralTermsModalProps) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
      if (event.key !== 'Tab') return;
      const dialog = dialogRef.current;
      const focusable = dialog?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], [tabindex="0"]'
      );
      if (!dialog || !focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!dialog.contains(document.activeElement) ||
        (event.shiftKey && document.activeElement === first)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [onClose]);

  return createPortal(
    <Backdrop onClick={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <Dialog ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <Header>
          <h2 id={titleId}>LOCUMSPACE REFERRAL REWARDS PROGRAM TERMS AND CONDITIONS</h2>
          <CloseButton ref={closeRef} type="button" onClick={onClose}>
            Close <span aria-hidden="true">×</span>
          </CloseButton>
        </Header>
        <Content tabIndex={0} role="region" aria-label="Referral program terms">
          <p><strong>Effective Date: 01/06/2026</strong></p>
          <p>
            These Terms and Conditions govern participation in the Locumspace Referral Rewards
            Program (“Program”). By participating in the Program, you agree to be legally bound
            by these Terms and Conditions and all decisions made by LocumSpace.
          </p>
          {referralTerms.map((section, index) => (
            <section key={section.title}>
              <h3>{index + 1}. {section.title}</h3>
              {section.clauses.map((clause) => (
                <div key={clause.number}>
                  <p>{clause.number} {clause.text}</p>
                  {clause.items ? (
                    <ol type="a">
                      {clause.items.map((item) => <li key={item}>{item}</li>)}
                    </ol>
                  ) : null}
                </div>
              ))}
            </section>
          ))}
          <p>
            For questions regarding the Program, contact:{' '}
            <a href="mailto:info@locumspace.co">info@locumspace.co</a>
          </p>
        </Content>
      </Dialog>
    </Backdrop>,
    document.getElementById('modal-root') || document.body
  );
};

export default ReferralTermsModal;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: grid;
  place-items: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.55);
`;

const Dialog = styled.section`
  width: min(800px, 100%);
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.blackBlackMain};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const Header = styled.header`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.greyGrey5};

  h2 {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    margin: 0;
    font-size: 20px;
    line-height: 1.4;
  }

  @media (max-width: 640px) {
    padding: 16px;
    h2 { font-size: 16px; }
  }
`;

const CloseButton = styled.button`
  flex-shrink: 0;
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.palette.greyGrey5};
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.mainBlue};
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  span { margin-left: 4px; font-size: 20px; }
  &:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
`;

const Content = styled.div`
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 24px;
  font-size: 16px;
  line-height: 1.7;
  overflow-wrap: anywhere;

  p { margin: 0 0 12px; }
  h3 { margin: 24px 0 12px; font-size: 18px; line-height: 1.5; }
  ol { list-style-type: lower-alpha; padding-left: 28px; margin: 0 0 16px; }
  li { margin-bottom: 6px; }
  a { color: ${({ theme }) => theme.palette.mainBlue}; text-decoration: underline; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.palette.mainBlue}; outline-offset: -3px; }

  @media (max-width: 640px) { padding: 16px; }
`;
